'use client';

import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

interface Options {
  videoRef: RefObject<HTMLVideoElement | null>;
  initialLoad: boolean;
  initialPlay: boolean;
  armOnIdle?: boolean;
}

export function useVideoArming({ videoRef, initialLoad, initialPlay, armOnIdle = false }: Options) {
  const [shouldLoadVideo, setShouldLoadVideo] = useState(initialLoad);
  const shouldPlayVideoOnLoadRef = useRef(initialPlay);

  const armVideo = useCallback((shouldPlay = true) => {
    shouldPlayVideoOnLoadRef.current = shouldPlay;
    setShouldLoadVideo(true);
  }, []);

  useEffect(() => {
    if (!armOnIdle) return;

    const win = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    if (win.requestIdleCallback) {
      const idleId = win.requestIdleCallback(() => armVideo(true), { timeout: 3000 });
      return () => win.cancelIdleCallback?.(idleId);
    }

    const timeoutId = setTimeout(() => armVideo(true), 1500);
    return () => clearTimeout(timeoutId);
  }, [armOnIdle, armVideo]);

  useEffect(() => {
    if (!shouldLoadVideo || !shouldPlayVideoOnLoadRef.current) {
      return;
    }

    videoRef.current?.play().catch(() => {});
  }, [shouldLoadVideo, videoRef]);

  return { shouldLoadVideo, armVideo };
}
